import { useRef, useState, type KeyboardEvent, type Ref } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { ModelSelector, type ModelOption } from '../ModelSelector/ModelSelector';
import styles from './PromptComposer.module.css';

/**
 * Figma: `Prompt Composer` (2057:23003) — the 2026-09 redesign for DeepDive AI,
 * which replaced the 560-wide card this file was first built from (760:93).
 * Nothing of that set survives in the file, so nothing of it survives here.
 *
 * `State=Default|Active|Generating|Answered` × `Focused=False|True`.
 *
 * `Default` and `Active` are one prop apart — whether the field holds text —
 * and both are read off the value rather than passed in. `Focused` is
 * `:focus-within` in the app and a forced prop for the parity story. That
 * leaves `status`, which is the two states the host decides: the agent is
 * `generating` (the eyebrow says so, send becomes stop) or has just
 * `answered` (the eyebrow says so, until the user types again).
 *
 * The eyebrow's resting text is the selected mode's description, which is
 * why the modes come in as `ModelOption`s rather than as a list of names — the
 * `Model selector` in the action row and the line above the card are the same
 * data twice.
 *
 * Still the system's first multi-line text input, and the field is now a
 * fixed 144 card rather than one that grows: Figma draws the whole height and
 * lets the text scroll inside it.
 */
export type PromptComposerStatus = 'idle' | 'generating' | 'answered';

export type PromptComposerProps = {
  /** The modes the selector offers. The composer's eyebrow shows the selected one's description. */
  modes: ModelOption[];
  /** The selected mode's id. Controlled. */
  mode: string;
  onModeChange?: (id: string) => void;
  /** Figma `State=Generating|Answered`; `idle` is `Default`/`Active`, read off the value. */
  status?: PromptComposerStatus;
  placeholder?: string;
  defaultValue?: string;
  /**
   * Controlled text. Omit to let the composer hold its own — DeepDive's empty
   * screen controls it so a `SuggestionPill` can fill the field.
   */
  value?: string;
  onValueChange?: (value: string) => void;
  /**
   * So a host can focus the field — the empty screen does, after a pill fills
   * it, since a prompt you cannot immediately edit or send is a dead end.
   */
  inputRef?: Ref<HTMLTextAreaElement>;
  /** Fires on Enter, or on pressing send. Receives the trimmed value. */
  onSend?: (text: string) => void;
  /** Fires when stop is pressed while `status` is `generating`. */
  onStop?: () => void;
  maxLength?: number;
  autoFocus?: boolean;
  /** Force Figma's `Focused=True` for documentation and visual regression only. */
  focused?: boolean;
  className?: string;
};

/**
 * Two refs on one element: the component's own, and the host's when it passed
 * one. A callback ref rather than a library — React has no built-in merge, and
 * this is the only place in the system that needs it.
 */
function mergeRefs(
  own: React.RefObject<HTMLTextAreaElement | null>,
  handed: Ref<HTMLTextAreaElement> | undefined,
) {
  return (node: HTMLTextAreaElement | null) => {
    own.current = node;
    if (typeof handed === 'function') handed(node);
    else if (handed) (handed as React.RefObject<HTMLTextAreaElement | null>).current = node;
  };
}

/** The eyebrow while the agent works, and once it has. Figma's own copy. */
const EYEBROW = {
  generating: 'Agent is thinking...',
  answered: 'Agent has answered',
} as const;

export function PromptComposer({
  modes,
  mode,
  onModeChange,
  status = 'idle',
  placeholder = 'Ask anything…',
  defaultValue = '',
  value: valueProp,
  onValueChange,
  inputRef,
  onSend,
  onStop,
  maxLength = 2000,
  autoFocus = false,
  focused,
  className,
}: PromptComposerProps) {
  const [ownValue, setOwnValue] = useState(defaultValue);
  const value = valueProp ?? ownValue;
  const field = useRef<HTMLTextAreaElement>(null);

  const setValue = (next: string) => {
    if (valueProp === undefined) setOwnValue(next);
    onValueChange?.(next);
  };

  const generating = status === 'generating';
  const canSend = value.trim().length > 0 && !generating;
  const selected = modes.find((option) => option.id === mode) ?? modes[0];

  /*
   * `answered` holds only until the user starts typing — Figma's `Answered`
   * variant draws the placeholder, and its `Active` sibling draws typed text
   * with the mode's description back in the eyebrow.
   */
  const eyebrow: PromptComposerStatus =
    status === 'answered' && value.length > 0 ? 'idle' : status;

  function submit() {
    if (!canSend) return;
    onSend?.(value.trim());
    setValue('');
  }

  /*
   * Enter sends, Shift+Enter opens a line. `isComposing` guards IME input —
   * without it, committing a candidate in a Japanese or Korean keyboard submits
   * the half-finished prompt.
   */
  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  }

  return (
    <form
      className={[styles.composer, className].filter(Boolean).join(' ')}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {/*
       * Figma `Eyebrow text`. A live region, so the agent's progress is
       * announced when it changes — the dot is decorative and the words carry
       * the meaning. Not in Figma.
       */}
      <div className={styles.eyebrow} aria-live="polite">
        {eyebrow !== 'idle' && (
          <span
            className={[styles.dot, eyebrow === 'answered' ? styles.dotAnswered : styles.dotThinking].join(' ')}
            aria-hidden
          />
        )}
        <span className={styles.eyebrowText}>
          {eyebrow === 'idle' ? selected?.description : EYEBROW[eyebrow]}
        </span>
      </div>

      {/* Figma `Field and Actions` — the white card. */}
      <div className={[styles.card, focused ? styles.focused : null].filter(Boolean).join(' ')}>
        <div className={styles.field}>
          <textarea
            ref={mergeRefs(field, inputRef)}
            className={styles.input}
            value={value}
            rows={1}
            maxLength={maxLength}
            placeholder={placeholder}
            aria-label="Prompt"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- caller opts in (autoFocus prop); used for the chat composer after an explicit user action.
            autoFocus={autoFocus}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>

        <div className={styles.actions}>
          <ModelSelector options={modes} value={selected?.id ?? mode} onValueChange={onModeChange} />

          {/*
           * The real `Button`, as Figma instances `Buttons/Button`. Send is
           * Primary and disabled until there is something to send; stop is
           * **Destructive** as of the 2026-09-20 update (it was Tertiary) and
           * stays enabled — stopping is the one thing the user still needs to
           * be able to do, and the red says it interrupts rather than waits.
           */}
          <Button
            hierarchy={generating ? 'destructive' : 'primary'}
            type={generating ? 'button' : 'submit'}
            onClick={generating ? onStop : undefined}
            disabled={!generating && !canSend}
            aria-label={generating ? 'Stop generating' : 'Send prompt'}
            iconLeading={<Icon name={generating ? 'stop' : 'arrowUp'} />}
          />
        </div>
      </div>
    </form>
  );
}
